/**
 * 計助ダッシュボード: フォーム変更をデバウンスしてコールバック実行
 * 2列・1要因列・2要因ブロックの各入力フォームで共通利用
 */

import { useEffect, useRef, type DependencyList } from 'react';

/** ストアの setGridDataDebounced（dashboardStore DEBOUNCE_MS）と揃える */
const DEFAULT_DELAY_MS = 280;

export interface UseDebouncedFormApplyOptions {
  delayMs?: number;
  /** true のとき、マウント直後の1回目はタイマーを張らない（フォーム⇔ストア同期ループ・点滅防止） */
  skipInitial?: boolean;
}

/**
 * form または applyForm が変わったら delayMs 後に applyForm() を実行する。
 * クリーンアップでタイマーを解除。
 */
export function useDebouncedFormApply(
  applyForm: () => void,
  deps: DependencyList,
  delayMsOrOptions: number | UseDebouncedFormApplyOptions = DEFAULT_DELAY_MS
): void {
  const opts: UseDebouncedFormApplyOptions =
    typeof delayMsOrOptions === 'number'
      ? { delayMs: delayMsOrOptions }
      : delayMsOrOptions;
  const delayMs = opts.delayMs ?? DEFAULT_DELAY_MS;
  const skipInitial = opts.skipInitial ?? false;
  const isFirstEffect = useRef(true);

  useEffect(() => {
    if (skipInitial && isFirstEffect.current) {
      isFirstEffect.current = false;
      return;
    }
    isFirstEffect.current = false;
    const id = setTimeout(applyForm, delayMs);
    return () => clearTimeout(id);
  }, deps);
}
